import { useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";

// The panel a lens gets when it declares a STRUCTURE rather than a flat grouping.
//
// `ManifestXrayPanel` renders one level and a fixed op shape, and its own header
// calls itself "the floor, not the ceiling". Measured against the readings that
// work, four things were missing, and all four are structural rather than cosmetic:
//
//   A CENSUS, not a count. "71 pages across 588 hosts read by 11 endpoints · 91
//   fetches" says whether a window is broad and shallow or narrow and deep. "126
//   runs" cannot.
//
//   TWO RAILS OVER THE SAME OPS. A reader enters from "what did this endpoint do"
//   as often as from "what happened to this thing". Both are projections of one set.
//
//   NESTING. container → item. Flattened to either level you get 588 rows or 12,
//   and neither is the reading.
//
//   SIGHTINGS. "seen 2× · changed between reads" is the most informative line on
//   the Research panel, because a target that changed under an agent is exactly the
//   case where its conclusion may not hold. It exists only by comparing calls.
//
// This does NOT try to be a bespoke panel. A hand-built lens that needs a custom
// affordance still writes its own file — that is what keeps the render stage
// containable. This is the shared vocabulary underneath them, so a declared lens
// reaches the shape without authoring JSX the CSP would not let it run anyway.

const EYEBROW = "text-[9px] uppercase tracking-[0.08em] leading-[1.2] text-[color:var(--eyebrow)]";
const NOTE = "text-[10px] leading-[1.4] text-[color:var(--muted)]";
const MONO = "font-mono text-[10.5px] leading-[1.5]";

function when(ts) {
  if (ts == null) return "";
  const d = ts instanceof Date ? ts : new Date(typeof ts === "number" ? ts : String(ts));
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// A sighting line, and the reason `null` is rendered rather than hidden: one
// observation has not shown a target stable, and silence there reads as stability.
function SightingNote({ sightings }) {
  if (!sightings || sightings.seen < 2) return null;
  const { seen, changed, observations } = sightings;
  return (
    <span className={NOTE}>
      seen {seen}×
      {changed === true ? (
        <span style={{ color: "var(--green)" }}> · changed between reads; latest observation below</span>
      ) : changed === false ? (
        " · same each time"
      ) : (
        // Not "unchanged". Nothing was compared, so nothing is known.
        ` · ${observations ? "not comparable" : "no observation reached the trace"}`
      )}
    </span>
  );
}

// A result in its own form. `table` is only used when the lens asked for it: a
// table is a claim about the shape of the output, and guessing it turns a wall of
// prose into a one-column grid that reads as parsed when it is not.
function Result({ value, as }) {
  const rows = useMemo(() => {
    if (as !== "table" || typeof value !== "string") return null;
    const lines = value.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 40);
    if (lines.length < 2) return null;
    const split = lines.map((l) => l.split(/\s{2,}|\t|\s*\|\s*/).filter(Boolean));
    const width = split[0].length;
    // Require a consistent column count. A ragged split is prose that happened to
    // contain spaces, and rendering it as a table invents structure.
    return width >= 2 && split.every((r) => r.length === width) ? split : null;
  }, [value, as]);

  if (value == null || value === "") return null;

  if (rows) {
    const [head, ...body] = rows;
    return (
      <div className="mt-[3px] min-w-0 overflow-x-auto rounded-[2px] border border-[color:var(--line-soft)]">
        <table className="w-full min-w-0 border-collapse">
          <thead>
            <tr>{head.map((h, i) => <th key={i} className={`${EYEBROW} px-[8px] py-[3px] text-left`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((r, i) => (
              <tr key={i} className="border-t border-[color:var(--line-soft)]">
                {r.map((c, j) => (
                  <td key={j} className={`${MONO} px-[8px] py-[2px] text-[color:var(--ink)]`}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const text = as === "json" ? safeFormat(value) : String(value);
  return (
    <pre className={`mt-[3px] min-w-0 whitespace-pre-wrap break-words rounded-[2px] border border-[color:var(--line-soft)] px-[8px] py-[4px] ${MONO} text-[color:var(--ink)]`}>
      {text.split("\n").slice(0, 18).join("\n")}
    </pre>
  );
}

function safeFormat(v) {
  try { return JSON.stringify(JSON.parse(String(v)), null, 2); } catch { return String(v); }
}

function OpRow({ op, lens, render, traceOf, labelOf, onSelectTrace }) {
  const missing = lens.explainMissing ? lens.explainMissing(op) : null;
  const resultValue = render.result?.from ? op[render.result.from] : null;
  // Three fields are already on screen as structure and must not repeat as rows:
  // the result (it has its own treatment), and the two identities that ARE the
  // section and item headers. Rendering `page ← /en/skills.md` directly under a
  // header reading `/en/skills.md` is noise that pushes the evidence down the page.
  const structural = new Set([render.result?.from, render.within, render.outer].filter(Boolean));
  const rows = (op.fields || []).filter((f) => !structural.has(f.name));
  return (
    <div className="min-w-0 pb-[7px]">
      <div className="flex min-w-0 items-baseline gap-[8px] pb-[2px]">
        <span className={`shrink-0 ${MONO} tabular-nums text-[color:var(--muted-soft)]`}>
          {when(traceOf?.get(op.promptId)?.timestamp ?? op.ts)}
        </span>
        <span className={`${NOTE} min-w-0 truncate`}>
          {op.endpointName && labelOf ? labelOf({ id: op.endpointId, label: op.endpointName }) : op.endpointName}
        </span>
        {op.promptId && onSelectTrace ? (
          <button
            type="button"
            onClick={() => onSelectTrace(op.promptId)}
            className="ml-auto shrink-0 cursor-pointer text-[10px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
          >
            open trace →
          </button>
        ) : null}
      </div>
      {rows.length ? (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] rounded-[2px] bg-[color:var(--control-bg)] py-[2px]">
          {rows.slice(0, 12).map((f, i) => (
            <div key={i} className={`flex min-w-0 items-baseline gap-[8px] px-[8px] ${MONO}`}>
              <span className={`${EYEBROW} w-[110px] shrink-0 truncate`}>{f.name}</span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[color:var(--ink)]">{f.value}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Result value={resultValue} as={render.result?.as} />
      {missing ? <span className={`${NOTE} block pt-[2px]`}>{missing}</span> : null}
    </div>
  );
}

export default function StructuredXrayPanel({ lens, structure, scan, traceOf, labelOf, onSelectTrace, windowNote = null }) {
  const [focus, setFocus] = useState(null);   // outer key
  const [actor, setActor] = useState(null);   // actor key

  // A structure is null both while the scan runs and when the lens read nothing, so
  // without the scan state those two render identically — a window still hydrating
  // would claim the lens found nothing, which is the absence-claim this whole panel
  // is built to avoid making. aria-busy is the single detectable "not ready" marker;
  // a visual-only cue is invisible to anything reading the DOM.
  if (scan?.status === "scanning") {
    return (
      <div aria-busy="true" className="grid min-h-0 h-full min-w-0 content-start grid-cols-[minmax(0,1fr)] gap-[4px] px-[12px] py-[8px]">
        <span className={NOTE}>Hydrating raw payloads {scan.done}/{scan.total}…</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--control-bg)]" />
        ))}
      </div>
    );
  }

  if (!structure || !structure.outer.length) {
    // The manifest owns the first sentence; the HOST owns the second, because
    // only it knows how much window the zero was measured over — "no X work in
    // this window" over 29 minutes of fresh traffic reads as a dead lens
    // without it.
    return (
      <div className="grid min-w-0 gap-[4px] px-[12px] py-[10px]">
        <span className={NOTE}>{lens.emptyNote}</span>
        {windowNote ? <span className={NOTE}>{windowNote}</span> : null}
      </div>
    );
  }
  const render = lens.render || { shape: "stream", result: {} };

  // Filtering by actor is a filter on the SAME ops, not a different query — the two
  // rails are projections of one set, and switching rails must not change the census
  // silently. So the census stays whole and the filter is stated.
  const shown = actor
    ? structure.outer
      .map((o) => ({
        ...o,
        ops: o.ops.filter((op) => (op.endpointId || op.endpointName) === actor),
        inner: (o.inner || []).map((i) => ({
          ...i, ops: i.ops.filter((op) => (op.endpointId || op.endpointName) === actor),
        })).filter((i) => i.ops.length),
      }))
      .filter((o) => o.ops.length)
    : structure.outer;

  const focused = focus ? shown.filter((o) => o.key === focus) : shown;

  return (
    // `h-full` is load-bearing, not decoration. The host clips the panel
    // (`overflow-clip`, because every panel owns its own scroll roots), so without a
    // definite height here the `minmax(0,1fr)` row has nothing to resolve against: it
    // grows to content, the ScrollAreas below are `h-full` OF THAT auto row and so
    // never overflow, and the reading is simply cut off with no way to scroll.
    // Measured on a 298px host: the panel laid out at 2875px and both scrollers
    // reported scrollHeight === clientHeight.
    <div className="grid min-h-0 h-full grid-rows-[auto_minmax(0,1fr)]">
      {/* The census: several orthogonal counts, because one number cannot say
          whether a window is broad and shallow or narrow and deep. */}
      <div className="min-w-0 border-b border-[color:var(--line-soft)] px-[12px] py-[6px]">
        <span className={NOTE}>
          {structure.census.map((c) => `${c.n} ${c.label}`).join(" · ")}
          {structure.totals.changed
            ? <span style={{ color: "var(--green)" }}>{` · ${structure.totals.changed} changed under the agent`}</span>
            : null}
          {actor ? ` · filtered to one endpoint of ${structure.actors.length}` : ""}
        </span>
      </div>

      <div className="grid min-h-0 grid-cols-[minmax(140px,210px)_minmax(0,1fr)]">
        {/* Left: the two rails. WHO, then the container tree. */}
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]">
          <div className="grid min-w-0 gap-[1px] px-[10px] py-[8px]">
            <span className={EYEBROW}>who</span>
            {structure.actors.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setActor((cur) => (cur === a.key ? null : a.key))}
                className={[
                  "flex min-w-0 items-baseline justify-between gap-[6px] rounded-[2px] px-[4px] py-[1px] text-left cursor-pointer",
                  actor === a.key ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                ].join(" ")}
              >
                <span className={`${NOTE} min-w-0 truncate text-[color:var(--ink)]`}>{a.label}</span>
                <span className={`${NOTE} shrink-0 tabular-nums`}>{a.ops.length}</span>
              </button>
            ))}

            <span className={`${EYEBROW} pt-[8px]`}>{render.shape === "tree" ? "containers" : "targets"}</span>
            {shown.map((o) => (
              <div key={o.key} className="grid min-w-0">
                <button
                  type="button"
                  onClick={() => setFocus((cur) => (cur === o.key ? null : o.key))}
                  className={[
                    "flex min-w-0 items-baseline justify-between gap-[6px] rounded-[2px] px-[4px] py-[1px] text-left cursor-pointer",
                    focus === o.key ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={`${MONO} min-w-0 truncate text-[color:var(--ink)]`}>{o.label}</span>
                  <span className={`${NOTE} shrink-0 tabular-nums`}>
                    {o.inner ? `${o.inner.length}` : `${o.ops.length}`}
                  </span>
                </button>
                {o.inner ? o.inner.slice(0, 8).map((i) => (
                  <span key={i.key} className={`${NOTE} min-w-0 truncate pl-[12px]`}>
                    {i.label}
                    {i.sightings.changed === true ? <span style={{ color: "var(--green)" }}> ·changed</span> : null}
                  </span>
                )) : null}
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Right: container → item → ops. */}
        <ScrollArea className="min-h-0 h-full">
          <div className="grid min-w-0 px-[12px] py-[8px]">
            {focused.map((o) => (
              <section key={o.key} className="grid min-w-0 gap-[4px] border-t border-[color:var(--line-soft)] py-[7px] first:border-t-0">
                <span className="flex min-w-0 flex-wrap items-baseline justify-between gap-[8px]">
                  <span className={`${MONO} min-w-0 truncate text-[color:var(--ink)]`}>{o.label}</span>
                  <span className={`${NOTE} shrink-0 tabular-nums`}>
                    {o.ops.length} run{o.ops.length === 1 ? "" : "s"}
                    {o.sessions > 1 ? ` · ${o.sessions} sessions` : ""}
                  </span>
                </span>
                {o.inner ? null : <SightingNote sightings={o.sightings} />}

                {o.inner
                  ? o.inner.map((i) => (
                    <div key={i.key} className="grid min-w-0 gap-[2px] border-l border-[color:var(--line-soft)] pl-[8px]">
                      <span className="flex min-w-0 flex-wrap items-baseline justify-between gap-[8px]">
                        <span className={`${MONO} min-w-0 truncate text-[color:var(--ink)]`}>{i.label}</span>
                        <span className={`${NOTE} shrink-0 tabular-nums`}>{i.ops.length}</span>
                      </span>
                      <SightingNote sightings={i.sightings} />
                      {i.ops.slice(0, 12).map((op, n) => (
                        <OpRow
                          key={op.key || n}
                          op={op} lens={lens} render={render}
                          traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace}
                        />
                      ))}
                    </div>
                  ))
                  : o.ops.slice(0, 24).map((op, n) => (
                    <OpRow
                      key={op.key || n}
                      op={op} lens={lens} render={render}
                      traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace}
                    />
                  ))}
              </section>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
